import { UsersIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetVendorDetails } from '@/api/visitors/get-vendor-details'
import { Badge } from '@/components/ui/badge'
import { BorderLoader } from '@/components/ui/loaders/border-loader'
import { Modal } from '@/components/ui/modal'
import { EMPTY_VALUE } from '@/constants/ui'
import { visitorStatusBadgeColorMap } from '@/models'
import {
  formatDateTime,
  formatPermissions,
  formatPermissionTimeRange,
} from '@/pages/visitors-dashboard/visitors-dashboard.utils'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import { COILink } from './coi-link'
import * as S from './vendor-details-modal.styled'
import { VendorDetailsModalProps } from './vendor-details-modal.types'

export const VendorDetailsModal = ({ vendorId, onClose }: VendorDetailsModalProps) => {
  const { t } = useTranslation(['visitors', 'common'])
  const building = useSelector(selectActiveBuilding)
  const timezone = building?.timezone ?? 'America/Chicago'

  const { data, isLoading } = useGetVendorDetails(vendorId)

  const visitor = data?.visitor
  const checkIns = data?.checkIns ?? []
  const timeRange = visitor ? formatPermissionTimeRange(visitor, timezone) : null

  return (
    <Modal
      title={t('visitors:detailsModal.guestVendorTitle', { type: t('visitors:modalTypes.vendor') })}
      headerIcon={UsersIcon}
      height="74.8rem"
      onClose={onClose}
    >
      {isLoading || !visitor ? (
        <BorderLoader align="absolute-center" />
      ) : (
        <S.Content>
          {visitor.notes && (
            <S.Section>
              <S.SectionTitle>{t('visitors:detailsModal.note')}</S.SectionTitle>
              <S.ItemsContainer>
                <S.Item>{visitor.notes}</S.Item>
              </S.ItemsContainer>
            </S.Section>
          )}

          <S.Section>
            <S.SectionTitle>{t('visitors:detailsModal.vendorInformation')}</S.SectionTitle>
            <S.ItemsContainer>
              <S.Item>
                <span>{t('visitors:detailsModal.vendorName')}</span>
                <S.ItemValue>{visitor.name || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.vendorStatus')}</span>
                <Badge
                  color={visitorStatusBadgeColorMap[visitor.status]}
                  size="small"
                  variant="light"
                >
                  {visitor.status.charAt(0).toUpperCase() + visitor.status.slice(1)}
                </Badge>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.email')}</span>
                <S.ItemValue>{visitor.email || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.phone')}</span>
                <S.ItemValue>{visitor.phone || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.company')}</span>
                <S.ItemValue>{visitor.company || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              {visitor.status === 'banned' && visitor.bannedReason && (
                <S.Item>
                  <span>{t('visitors:detailsModal.banReason')}</span>
                  <S.ItemValue>{visitor.bannedReason}</S.ItemValue>
                </S.Item>
              )}
            </S.ItemsContainer>
          </S.Section>

          <S.Section>
            <S.SectionTitle>{t('visitors:detailsModal.permissionAndVisitDetails')}</S.SectionTitle>
            <S.ItemsContainer>
              <S.Item>
                <span>{t('visitors:detailsModal.permissionType')}</span>
                <S.ItemValue>
                  {formatPermissions(visitor, timezone)}
                  {timeRange && `, ${timeRange}`}
                </S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.startTime')}</span>
                <S.ItemValue>
                  {visitor.startTime
                    ? formatDateTime(visitor.startTime, timezone).time
                    : EMPTY_VALUE}
                </S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.endTime')}</span>
                <S.ItemValue>
                  {visitor.endTime ? formatDateTime(visitor.endTime, timezone).time : EMPTY_VALUE}
                </S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.createdBy')}</span>
                <S.ItemValue>{visitor.createdBy || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.coi')}</span>
                <COILink
                  filePath={visitor.coi}
                  visitorName={visitor.name || t('visitors:detailsModal.vendorDefaultName')}
                />
              </S.Item>
              {building?.customAccessAvailable && (
                <S.Item>
                  <span>{building.customAccessName}</span>
                  <S.ItemValue>
                    {visitor.customAccess ? t('common:booleans.yes') : t('common:booleans.no')}
                  </S.ItemValue>
                </S.Item>
              )}
              {/* Note: As it was described in EL-14633 - keylessAccess is temporarly treated as
              physical key access, // and keyAccess is treated as keyless integration */}
              {building?.physicalKeyAvailable && (
                <S.Item>
                  <span>{t('visitors:detailsModal.physicalKeyRelease')}</span>
                  <S.ItemValue>
                    {visitor.keylessAccess ? t('common:booleans.yes') : t('common:booleans.no')}
                  </S.ItemValue>
                </S.Item>
              )}
              <S.Item>
                <span>{t('visitors:detailsModal.vendorId')}</span>
                <S.ItemValue>{visitor.vendorId || visitor.guestId || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
            </S.ItemsContainer>
          </S.Section>

          {checkIns.length > 0 && (
            <S.Section>
              <S.SectionTitle>{t('visitors:detailsModal.checkInLog')}</S.SectionTitle>
              <S.ItemsContainer>
                {checkIns.map((log, index) => {
                  const { date, time } = formatDateTime(log.date, timezone)
                  return (
                    <S.Item key={index}>
                      <span>{date}</span>
                      <S.ItemValue>{time}</S.ItemValue>
                    </S.Item>
                  )
                })}
              </S.ItemsContainer>
            </S.Section>
          )}
        </S.Content>
      )}
    </Modal>
  )
}
